/**
 * ShareRolePicker — the Role tab of both Share dialogs (document and folder).
 *
 * Library LB6-T3, acceptance L6-05 (decision 27 / B3: a tenant's CUSTOM roles
 * may be named as a share subject, as the shipped ones already are). The
 * options are the tenant's roles AS THE USERS RAIL SERVES THEM —
 * GET /users/roles through `useRoles()` (lib/use-roles.ts), the one role
 * reader the Roles screen and the role pickers use — shipped and custom alike,
 * retired ones left out. The value is the role's KEY (principle #5: reference
 * by key), which is what the server matches a role share on
 * (Principal.roleKeys, OQ-3).
 *
 * It replaced a literal list of the five shipped role keys in each dialog,
 * which no tenant role could ever appear in.
 *
 * A roles list that cannot be read is said so, with nothing to pick — never a
 * fallback list.
 *
 * WHO GETS THE ROLE TAB (LB6-T3 review BF-07). The options come from
 * GET /users/roles, which declares read:roles — not share:library, the key the
 * Share control asks. Every shipped role that holds share:library holds
 * read:roles too, but a tenant's custom role may hold the one without the
 * other, and its holder was shown a Role tab whose list always failed
 * ("The roles could not be loaded") — a control rendered for someone its
 * route refuses (L6-04). So the tab is drawn only for a person who holds
 * read:roles (`useShareRoleTab`); everyone else shares with a user, group or
 * department.
 */
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useRoles } from '@/lib/use-roles';
import { useHasPermission } from '@/lib/use-has-permission';

/** Does the person get the Role tab? Exactly when they may read its options (GET /users/roles → read:roles). */
export function useShareRoleTab(): boolean {
  return useHasPermission('read', 'roles');
}

export interface ShareRolePickerProps {
  /** The trigger's id (each dialog keeps its own, for its label). */
  id: string;
  /** The picked role's key, or '' while none is picked. */
  value: string;
  onChange: (roleKey: string) => void;
}

export function ShareRolePicker({ id, value, onChange }: ShareRolePickerProps) {
  const { roles, isLoading, error } = useRoles();
  const live = roles.filter((r) => r.retiredAt === null);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Role</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Pick a role" />
        </SelectTrigger>
        <SelectContent>
          {isLoading && (
            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
          )}
          {!isLoading && !!error && (
            <SelectItem value="__error__" disabled>The roles could not be loaded</SelectItem>
          )}
          {live.map((r) => (
            <SelectItem key={r.key} value={r.key} data-role-kind={r.kind}>
              {r.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!isLoading && !!error && (
        <p className="text-xs text-destructive" data-testid="share-roles-error">
          The roles could not be loaded. Share with a user, group or department, or try again.
        </p>
      )}
    </div>
  );
}
